/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 * */

/**
 * EmptyStateCard
 *
 * @file EmptyStateCard.jsx
 * @author Mohamed yasar arabath M
 * @since 0.0.1
 */

import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import Icon from '../icons/Icon';
import mixins from '../../assets/styles/mixins.scss';

const InfoPanelRoot = styled(Box)({
  '&&': {
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    background: mixins.chalkWhiteFont,
    borderRadius: 16,
    width: '100%'
  }
});

const InfoPanelContent = styled(Box)({
  '&&': {
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 24
  }
});

const InfoPanelBody = styled(Box)({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    alignItems: 'center',
    width: '100%'
  }
});

const IconWrapper = styled(Box)({
  '&&': {
    background: mixins.badgeBgColor,
    borderRadius: 1000,
    padding: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
});

const IconWrapperInner = styled('span')({
  display: 'inline-flex',
  width: 40,
  height: 40,
  color: mixins.amethystColor,
  '& svg': {
    width: 40,
    height: 40
  }
});

const TextContainer = styled(Box)({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    alignItems: 'center'
  }
});

const TitleText = styled(Typography)({
  '&&': {
    color: mixins.color_text_primary,
    textAlign: 'center',
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight500,
    fontSize: mixins.fontSize20,
    lineHeight: '28px',
    letterSpacing: '-0.4px'
  }
});

const DescriptionText = styled(Typography)({
  '&&': {
    color: mixins.color_text_primary,
    textAlign: 'center',
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight500,
    fontSize: mixins.fontSize14,
    lineHeight: '22px'
  }
});

const SubDescriptionText = styled(Typography)({
  '&&': {
    color: mixins.greyScaleColor,
    textAlign: 'center',
    fontWeight: mixins.fontWeight500,
    fontSize: mixins.fontSize12,
    lineHeight: '20px'
  }
});

const CourseCardDescriptionText = styled(Typography)({
  '&&': {
    color: mixins.color_text_primary,
    textAlign: 'center',
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight500,
    fontSize: mixins.fontSize20,
    lineHeight: '28px',
    letterSpacing: '-0.4px'
  }
});

TitleText.defaultProps = { variant: 'inherit' };
DescriptionText.defaultProps = { variant: 'inherit' };
SubDescriptionText.defaultProps = { variant: 'inherit' };
CourseCardDescriptionText.defaultProps = { variant: 'inherit' };

const CancelButton = styled(Button)({
  '&&': {
    borderRadius: '1000px',
    padding: 8,
    border: `2px solid ${mixins.color_text_primary}`,
    backgroundColor: 'transparent',
    color: mixins.color_text_primary,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight500,
    lineHeight: '24px',
    maxWidth: 80,
    height: 40,
    boxSizing: 'border-box',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'transparent',
      color: mixins.color_text_primary
    }
  }
});

const RetryButton = styled(Button)({
  '&&': {
    borderRadius: '1000px',
    padding: 8,
    boxSizing: 'border-box',
    backgroundColor: mixins.amethystColor,
    color: mixins.chalkWhiteFont,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight500,
    lineHeight: '24px',
    textTransform: 'none',
    fontFamily: mixins.fontFamilyPlusSans,
    height: 40,
    border: 'none',
    '&:hover': {
      backgroundColor: mixins.amethystColor,
      color: mixins.chalkWhiteFont
    }
  }
});

const InviteStudentsCTA = styled(Button)({
  '&&': {
    borderRadius: '1000px',
    // MUI v7: merged padding (was 8px button + 0 8px on .MuiButton-label span)
    padding: '8px 11px 10px 11px',
    minWidth: 'unset',
    border: `2px solid ${mixins.navyColor}`,
    backgroundColor: 'transparent',
    color: mixins.navyColor,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight500,
    fontFamily: mixins.fontFamilyPlusSans,
    lineHeight: '24px',
    height: 40,
    boxSizing: 'border-box',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: mixins.flaggedChipColor,
      color: mixins.navyColor
    }
  }
});

const ActionWrapper = styled('div')({
  '&&': {
    display: 'flex',
    gap: 8
  }
});

const TitleTextLeft = styled(TitleText)({
  '&&': {
    alignSelf: 'flex-start',
    textAlign: 'left',
    width: '100%'
  }
});

TitleTextLeft.defaultProps = { variant: 'inherit' };


const EmptyStateCard = (props) => {
  const {
    iconName,
    title,
    description,
    subDescription,
    cancelCTA,
    retryCTA,
    showActions,
    inviteStudentsCTA,
    titleAlign = 'center'
  } = props;
  const isTitleLeft = titleAlign === 'left';

  const renderDescription = () => {
    if (!description) {
      return null;
    }

    if (!isTitleLeft) {
      return (
        <DescriptionText>
          {description}
        </DescriptionText>
      );
    }

    return (
      <CourseCardDescriptionText>
        {description}
      </CourseCardDescriptionText>
    );
  };

  return (
    <InfoPanelRoot>
      {isTitleLeft && title && (
        <TitleTextLeft>
          {title}
        </TitleTextLeft>
      )}
      <InfoPanelContent>
        <InfoPanelBody>
          <IconWrapper>
            <IconWrapperInner>
              <Icon name={iconName} />
            </IconWrapperInner>
          </IconWrapper>
          <TextContainer>
            {(!isTitleLeft && title) ? (
              <TitleText>
                {title}
              </TitleText>
            ) : null}
            {renderDescription()}

            {subDescription ?
              (
                <SubDescriptionText>
                  {subDescription}
                </SubDescriptionText>
              ) : null}
          </TextContainer>
          {showActions &&
            <ActionWrapper>
              {cancelCTA &&
              (
              <CancelButton onClick={cancelCTA.onClick}>
                {cancelCTA.text}
              </CancelButton>
              )}

              {retryCTA &&
               (
               <RetryButton onClick={retryCTA.onClick}>
                 {retryCTA.text}
               </RetryButton>
               )}
              {inviteStudentsCTA &&
              (
              <InviteStudentsCTA onClick={inviteStudentsCTA.onClick}>
                {inviteStudentsCTA.text}
              </InviteStudentsCTA>
              )}
            </ActionWrapper>}
        </InfoPanelBody>
      </InfoPanelContent>
    </InfoPanelRoot>
  );
};


EmptyStateCard.defaultProps = {
  iconName: 'CertificateIcon',
  description: '',
  subDescription: '',
  cancelCTA: null,
  retryCTA: null,
  showActions: false,
  inviteStudentsCTA: null,
  titleAlign: 'center'
};

EmptyStateCard.propTypes = {
  iconName: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  subDescription: PropTypes.string,
  cancelCTA: PropTypes.object,
  retryCTA: PropTypes.object,
  showActions: PropTypes.bool,
  inviteStudentsCTA: PropTypes.object,
  titleAlign: PropTypes.string
};

export default EmptyStateCard;
